import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { bookings as bookingsApi } from "../damsApi";
import { Calendar, MapPin, Loader2, FileText, ChevronRight, Clock, Wallet } from "lucide-react";
import { toast } from "sonner";
import { ExtraMileageWarning } from "../components/DepositPolicy";

const statusBadge = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "confirmed") return "badge badge-blue";
  if (s === "pending_payment") return "badge badge-amber";
  if (s === "active") return "badge badge-green";
  if (s === "completed") return "badge badge-gray";
  if (s === "cancelled" || s === "canceled") return "badge badge-red";
  return "badge badge-gray";
};

const fmtStatus = (s) =>
  String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function MyBookings() {
  const { user, loading: authLoading } = useCustomerAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await bookingsApi.list();
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load bookings:", err);
      toast.error("Could not load your bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/sign-in", { state: { from: "/my-bookings" } });
      return;
    }
    if (user) refresh();
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewReceipt = (b) => {
    try {
      window.open(bookingsApi.receiptPdfUrl(b.id), "_blank", "noopener,noreferrer");
    } catch (_) {
      toast.error("Could not open receipt");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="py-16 text-center text-gray-400">
        <Loader2 className="animate-spin inline mr-2" size={18} /> Loading...
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="dams-brand-sub text-red-500 mb-1">Account</p>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">My bookings</h1>
        </div>
        <Link
          to="/profile"
          className="text-sm font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"
        >
          Profile <ChevronRight size={14} />
        </Link>
      </div>

      {bookings.length === 0 ? (
        <div className="card-soft p-12 text-center" data-testid="bookings-empty">
          <p className="text-gray-500 mb-4">You haven't made any reservations yet.</p>
          <Link
            to="/cars"
            className="pill-btn primary inline-block"
            style={{ padding: "0.7rem 1.4rem" }}
          >
            {t("hero.ctaSearch")}
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {bookings.map((b) => {
            const pickupName =
              typeof b.pickup_location === "object"
                ? b.pickup_location?.name
                : b.pickup_location;
            return (
              <div key={b.id} className="card-soft p-5">
                <div className="flex items-start gap-3">
                  {b.car_image ? (
                    <img src={b.car_image} alt="" className="car-thumb" />
                  ) : (
                    <div className="car-thumb" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold">{b.car_name}</h3>
                      <span className={statusBadge(b.status)}>{fmtStatus(b.status)}</span>
                      {b.payment_status === "pending" && b.payment_method === "stripe" && (
                        <span className="badge badge-amber inline-flex items-center gap-1">
                          <Clock size={11} /> Payment pending
                        </span>
                      )}
                      {Number(b.deposit || 0) > 0 && (
                        <span
                          data-testid={`deposit-pill-${b.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full"
                          style={{ background: "#eaf3ff", color: "#0a3d80", border: "1px solid #b8d5f5" }}
                        >
                          <Wallet size={10} /> ${Number(b.deposit).toFixed(2)} refundable deposit
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reservation #{b.id?.slice(-8).toUpperCase()}
                    </p>
                    <div className="text-sm text-gray-700 mt-2 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={13} /> {b.pickup_date} → {b.dropoff_date}
                      </span>
                      {pickupName && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={13} className="text-red-500" /> {pickupName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-xl font-extrabold text-red-600">
                      ${Number(b.total_price || 0).toFixed(2)}
                    </p>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-1">
                      {b.payment_method}
                    </p>
                  </div>
                </div>
                <ExtraMileageWarning
                  km={b.extra_mileage_km}
                  rate={b.extra_mileage_rate}
                  fee={b.extra_mileage_fee}
                />
                <div className="subtle-divider" />
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => viewReceipt(b)}
                    className="pill-btn outline inline-flex items-center gap-1"
                    style={{ padding: "0.45rem 0.9rem", fontSize: "0.8rem" }}
                    data-testid={`view-receipt-${b.id}`}
                  >
                    <FileText size={14} /> View receipt
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
